import React from 'react';

// JUCO Pipeline Terms of Service content (shared by the /terms page and the modal)
const INTRO = [
  'Welcome to JUCO Pipeline. These Terms of Service govern your access to and use of the JUCO Pipeline website, platform, database, profiles, statistics, video features, scouting information, subscription services, and related services.',
  'By accessing or using JUCO Pipeline, you agree to these Terms. If you do not agree, do not use the platform.',
];

const SECTIONS = [
  {
    title: '1. About JUCO Pipeline',
    body: [
      { type: 'p', text: 'JUCO Pipeline is a platform designed to help junior college baseball players, coaches, scouts, agents, recruiters, and other baseball-related users access player information, statistics, videos, profiles, and recruiting-related tools.' },
      { type: 'p', text: 'JUCO Pipeline does not guarantee recruiting opportunities, scholarship offers, professional interest, player placement, or any specific athletic outcome.' },
    ],
  },
  {
    title: '2. Who May Use the Platform',
    body: [
      { type: 'p', text: 'You may use JUCO Pipeline only if you are legally able to enter into these Terms. Users may include, but are not limited to:' },
      { type: 'ul', items: ['Junior college baseball players', 'Coaches', 'Scouts', 'Recruiters', 'Agents', 'Parents or guardians', 'Baseball organizations', 'Other approved users'] },
      { type: 'p', text: 'If you are under 18, you may use the platform only with permission from a parent or legal guardian.' },
    ],
  },
  {
    title: '3. User Accounts',
    body: [
      { type: 'p', text: 'Certain features may require you to create an account. You agree to provide accurate information and keep your login credentials secure.' },
      { type: 'p', text: 'You are responsible for all activity that occurs under your account.' },
      { type: 'p', text: 'JUCO Pipeline may suspend or terminate accounts that provide false information, misuse the platform, violate these Terms, or create risk for the company, users, or platform.' },
    ],
  },
  {
    title: '4. Player Profiles and Information',
    body: [
      { type: 'p', text: 'JUCO Pipeline may display player profiles, statistics, school information, roster data, videos, scouting-related information, and other baseball-related content.' },
      { type: 'p', text: 'Some information may come from publicly available sources, third-party data providers, schools, users, or direct submissions.' },
      { type: 'p', text: 'JUCO Pipeline makes reasonable efforts to provide accurate information, but we do not guarantee that all player data, statistics, rankings, schools, eligibility information, or profile details are complete, current, or error-free.' },
      { type: 'p', text: 'Players may be allowed to claim or update their profiles, subject to JUCO Pipeline’s verification process and platform rules.' },
    ],
  },
  {
    title: '5. User-Submitted Content',
    body: [
      { type: 'p', text: 'Users may be able to submit content, including but not limited to:' },
      { type: 'ul', items: ['Profile information', 'Photos', 'Videos', 'Statistics', 'Biographical details', 'Scouting notes', 'Messages', 'Comments', 'Other baseball-related materials'] },
      { type: 'p', text: 'By submitting content to JUCO Pipeline, you represent that:' },
      { type: 'ol', items: ['You have the right to submit it.', 'The content is accurate to the best of your knowledge.', 'The content does not violate another person’s rights.', 'The content does not contain unlawful, misleading, defamatory, abusive, obscene, or harmful material.'] },
      { type: 'p', text: 'JUCO Pipeline may remove, edit, refuse, or restrict content at any time.' },
    ],
  },
  {
    title: '6. License to Use Submitted Content',
    body: [
      { type: 'p', text: 'When you submit content to JUCO Pipeline, you grant JUCO Pipeline a non-exclusive, worldwide, royalty-free license to use, display, reproduce, distribute, modify, and publish that content in connection with operating, promoting, improving, and providing the platform.' },
      { type: 'p', text: 'This includes displaying player information, videos, photos, and related baseball content to users of the platform.' },
      { type: 'p', text: 'You retain ownership of content you legally own, but JUCO Pipeline has permission to use it as described above.' },
    ],
  },
  {
    title: '7. Prohibited Conduct',
    body: [
      { type: 'p', text: 'You agree not to:' },
      { type: 'ul', items: ['Use the platform for unlawful purposes', 'Submit false, misleading, or fraudulent information', 'Harass, threaten, abuse, or impersonate others', 'Scrape, copy, export, or mass-download platform data without permission', 'Attempt to bypass security or access restrictions', 'Share login credentials with unauthorized users', 'Use the platform to spam, solicit, or misuse player/coach contact information', 'Upload malware, viruses, or harmful code', 'Interfere with the operation of the website', 'Use JUCO Pipeline data to create a competing database, directory, scouting platform, or recruiting platform without written permission'] },
      { type: 'p', text: 'JUCO Pipeline may suspend or terminate access for violations.' },
    ],
  },
  {
    title: '8. Subscriptions and Paid Services',
    body: [
      { type: 'p', text: 'JUCO Pipeline may offer paid subscriptions, memberships, premium access, team accounts, coach accounts, scouting tools, or other paid services.' },
      { type: 'p', text: 'By purchasing a paid service, you agree to pay the listed fees and any applicable taxes.' },
      { type: 'p', text: 'Subscription details, pricing, billing periods, renewal terms, cancellation rules, and included features will be provided at checkout or within your account.' },
      { type: 'p', text: 'Unless otherwise stated, subscription fees are non-refundable.' },
      { type: 'p', text: 'JUCO Pipeline may change pricing or subscription features in the future. If pricing changes apply to your subscription, we will make reasonable efforts to notify you.' },
    ],
  },
  {
    title: '9. Team or Group Accounts',
    body: [
      { type: 'p', text: 'JUCO Pipeline may offer team, school, organization, or group plans that allow multiple users under one subscription.' },
      { type: 'p', text: 'The account owner or administrator is responsible for managing invited users and ensuring that all users follow these Terms.' },
      { type: 'p', text: 'JUCO Pipeline may limit the number of users, seats, or access rights based on the selected plan.' },
    ],
  },
  {
    title: '10. No Recruiting, Scholarship, or Employment Guarantee',
    body: [
      { type: 'p', text: 'JUCO Pipeline is an information and visibility platform. We do not guarantee that any player will receive:' },
      { type: 'ul', items: ['Recruiting interest', 'Scholarship offers', 'College placement', 'Professional scouting interest', 'Agent representation', 'NIL opportunities', 'Employment or athletic opportunities'] },
      { type: 'p', text: 'Users are responsible for independently verifying information and making their own recruiting, scouting, and athletic decisions.' },
    ],
  },
  {
    title: '11. Third-Party Information and Links',
    body: [
      { type: 'p', text: 'The platform may include information from third-party sources or links to third-party websites.' },
      { type: 'p', text: 'JUCO Pipeline is not responsible for third-party websites, data providers, services, content, policies, or accuracy.' },
      { type: 'p', text: 'Accessing third-party links or relying on third-party information is done at your own risk.' },
    ],
  },
  {
    title: '12. Intellectual Property',
    body: [
      { type: 'p', text: 'JUCO Pipeline, including its name, logo, design, database structure, platform features, software, graphics, text, and original content, is owned by JUCO Pipeline or its licensors.' },
      { type: 'p', text: 'You may not copy, reproduce, modify, distribute, sell, or exploit any part of the platform without written permission.' },
      { type: 'p', text: 'Limited personal or internal use of the platform is allowed only as permitted by these Terms.' },
    ],
  },
  {
    title: '13. Data Access and Restrictions',
    body: [
      { type: 'p', text: 'JUCO Pipeline provides access to player and baseball-related information for legitimate baseball, recruiting, scouting, and related purposes. You may not use platform data to:' },
      { type: 'ul', items: ['Build a competing product', 'Create a separate database', 'Resell data', 'Mass contact players, coaches, or schools', 'Violate NCAA, NJCAA, school, league, or applicable recruiting rules', 'Engage in unlawful or misleading activity'] },
      { type: 'p', text: 'JUCO Pipeline may limit, restrict, or revoke data access at any time.' },
    ],
  },
  {
    title: '14. Privacy',
    body: [
      { type: 'p', text: 'Your use of JUCO Pipeline is also governed by our Privacy Policy.' },
      { type: 'p', text: 'The Privacy Policy explains how we collect, use, store, and share information.' },
      { type: 'p', text: 'You should review the Privacy Policy before using the platform.' },
    ],
  },
  {
    title: '15. Platform Availability',
    body: [
      { type: 'p', text: 'JUCO Pipeline may update, modify, suspend, or discontinue parts of the platform at any time.' },
      { type: 'p', text: 'We do not guarantee that the platform will always be available, uninterrupted, secure, or error-free.' },
      { type: 'p', text: 'We are not responsible for losses caused by downtime, technical issues, data errors, service interruptions, or third-party failures.' },
    ],
  },
  {
    title: '16. Disclaimer of Warranties',
    body: [
      { type: 'p', text: 'JUCO Pipeline is provided on an “as is” and “as available” basis.' },
      { type: 'p', text: 'To the fullest extent allowed by law, JUCO Pipeline makes no warranties, express or implied, including warranties of accuracy, reliability, fitness for a particular purpose, merchantability, non-infringement, or availability.' },
      { type: 'p', text: 'You use the platform at your own risk.' },
    ],
  },
  {
    title: '17. Limitation of Liability',
    body: [
      { type: 'p', text: 'To the fullest extent allowed by law, JUCO Pipeline and its owners, officers, employees, contractors, partners, and affiliates will not be liable for indirect, incidental, special, consequential, punitive, or lost-profit damages. This includes damages related to:' },
      { type: 'ul', items: ['Recruiting decisions', 'Incorrect player information', 'Missed opportunities', 'Account suspension', 'Platform downtime', 'Data loss', 'Third-party conduct', 'Unauthorized access'] },
      { type: 'p', text: 'To the extent liability cannot be excluded, JUCO Pipeline’s total liability will not exceed the amount paid by you to JUCO Pipeline in the three months before the claim arose, or $100 if you did not pay for the service.' },
    ],
  },
  {
    title: '18. Indemnification',
    body: [
      { type: 'p', text: 'You agree to defend, indemnify, and hold harmless JUCO Pipeline and its owners, officers, employees, contractors, and affiliates from any claims, damages, losses, liabilities, costs, or expenses arising from:' },
      { type: 'ul', items: ['Your use of the platform', 'Your submitted content', 'Your violation of these Terms', 'Your violation of another person’s rights', 'Your misuse of player, coach, school, or recruiting information'] },
    ],
  },
  {
    title: '19. Termination',
    body: [
      { type: 'p', text: 'JUCO Pipeline may suspend or terminate your account or access to the platform at any time if we believe you violated these Terms, created risk for the platform, misused data, failed to pay fees, or acted in a way that harms JUCO Pipeline or its users.' },
      { type: 'p', text: 'You may stop using the platform at any time.' },
      { type: 'p', text: 'Certain provisions of these Terms will continue after termination, including intellectual property, payment obligations, disclaimers, liability limits, indemnification, and dispute provisions.' },
    ],
  },
  {
    title: '20. Changes to These Terms',
    body: [
      { type: 'p', text: 'JUCO Pipeline may update these Terms from time to time.' },
      { type: 'p', text: 'If we make material changes, we may notify users through the platform, by email, or by updating the effective date.' },
      { type: 'p', text: 'Continued use of the platform after changes means you accept the updated Terms.' },
    ],
  },
  {
    title: '21. Governing Law',
    body: [
      { type: 'p', text: 'These Terms are governed by the laws of the State of Florida, without regard to conflict of law rules.' },
      { type: 'p', text: 'Any disputes will be handled in the state or federal courts located in Duval County, Florida, unless otherwise required by law.' },
    ],
  },
  {
    title: '22. Contact Information',
    body: [
      { type: 'p', text: 'Questions about these Terms may be directed to:' },
      { type: 'p', text: 'JUCO Pipeline' },
      { type: 'p', text: 'Email: jucopipeline@gmail.com' },
    ],
  },
];

export const TermsContent = () => {
  return (
    <div className="text-left">
      {/* Intro */}
      <div className="space-y-4 mb-10">
        {INTRO.map((text, i) => (
          <p key={i} className="text-jp-muted leading-relaxed">
            {text}
          </p>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-8">
        {SECTIONS.map((section) => (
          <section key={section.title} className="pt-8 border-t border-jp-line first:pt-0 first:border-t-0">
            <h2 className="text-lg sm:text-xl font-bold text-jp-ink mb-3">
              {section.title}
            </h2>
            <div className="space-y-3">
              {section.body.map((block, i) => {
                if (block.type === 'p') {
                  return (
                    <p key={i} className="text-jp-muted leading-relaxed">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === 'ul') {
                  return (
                    <ul key={i} className="list-disc pl-6 space-y-1 text-jp-muted marker:text-jp-accent">
                      {block.items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === 'ol') {
                  return (
                    <ol key={i} className="list-decimal pl-6 space-y-1 text-jp-muted marker:text-jp-accent marker:font-semibold">
                      {block.items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ol>
                  );
                }
                return null;
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default TermsContent;
